// @flow

// Libs
import _map from 'lodash/map'
import _keyBy from 'lodash/keyBy'
import _omit from 'lodash/omit'
import _reduce from 'lodash/reduce'

// Types
import type { Operation, State, Reducers, OperationsById, CreateOperationArgs } from '../types'

// Action Types Composer
import composeActionTypes from './composeActionTypes'

// Local Types
type Args<T> = {
  model: string,
  branch: string,
  createOperation: (object: CreateOperationArgs<T>) => Operation<T>
}

// Exports
export default function composeReducers<T: Object> ({
  model: modelName,
  branch,
  createOperation,
  }: Args<T>): Reducers<T> {
  const actionTypes = composeActionTypes({ model: modelName })

  function addOperation (state: State<T>, payload: Array<Operation<T>>): State<T> {
    return {
      ...state,
      [branch]: {
        ...state[branch],
        ..._keyBy(payload, 'operationId'),
      },
    }
  }

  function cancelOperation (state: State<T>, payload: Operation<T>): State<T> {
    return {
      ...state,
      [branch]: _omit(state[branch], payload.operationId),
    }
  }

  function cancelOperations (state: State<T>, payload: Array<Operation<T>>): State<T> {
    const operationIds = _map(payload, 'operationId')

    return {
      ...state,
      [branch]: _omit(state[branch], ...operationIds),
    }
  }

  function submitOperation (state: State<T>, operation: Operation<T>): State<T> {
    const operationId: string = operation.operationId
    const currentOperation = state[branch][operationId]

    return {
      ...state,
      [branch]: {
        ...state[branch],
        [operationId]: {
          ...currentOperation,
          ...operation,
          submitting: true,
          errors: [],
        },
      },
    }
  }

  function submitOperations (state: State<T>, operations: Array<Operation<T>>): State<T> {
    const currentOperations = state[branch]
    const nextOperations = _reduce(
      operations,
      (acc: OperationsById<T>, operation: Operation<T>): OperationsById<T> => {
        const operationId: string = operation.operationId
        const currentOperation = acc[operationId]

        acc[operationId] = {
          ...currentOperation,
          ...operation,
          submitting: true,
          errors: [],
        }

        return acc
      },
      { ...currentOperations }
    )

    return {
      ...state,
      [branch]: {
        ...state[branch],
        ...nextOperations,
      },
    }
  }

  function updateOperation (state: State<T>, operation: Operation<T>): State<T> {
    const operationId: string = operation.operationId
    const currentOperation: Operation<T> = state[branch][operationId]
    const model: T = operation.model

    return {
      ...state,
      [branch]: {
        ...state[branch],
        [operationId]: {
          ...currentOperation,
          ...operation,
          model: Object.assign({}, currentOperation.model, model),
        },
      },
    }
  }

  return {
    [actionTypes.add()]: addOperation,
    [actionTypes.edit()]: addOperation,
    [actionTypes.delete()]: addOperation,
    [actionTypes.read()]: addOperation,
    [actionTypes.updateOperation()]: updateOperation,
    [actionTypes.cancelOperation()]: cancelOperation,
    [actionTypes.cancelOperations()]: cancelOperations,
    [actionTypes.submitOperation()]: submitOperation,
    [actionTypes.submitOperations()]: submitOperations,
  }
}
